import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { InventoryItem, PricingResult, SizeClass } from '@wrapforce/shared';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import type { DraftLine } from '../NewQuotePage';
import {
  useCeramicPricingProfile, useVehicleCatalogTrimDetail, useWorkspaceVehicleById,
  type VehiclePricingSource,
} from './pricingApi';
import { useVehicleServiceQuantities, usePutVehicleServiceQuantity } from '@/features/settings/pricing-v2/api';
import { useBrands, useInventory } from '@/features/inventory/api';
import { useLocaleFormatters } from '@/lib/format';
import {
  VehiclePicker, vehicleSelectionFromTrimDetail, vehicleSelectionFromWorkspaceVehicleItem,
  type CustomerVehicleHint, type VehicleSelection,
} from './VehiclePicker';
import { PricingBreakdownSidebar } from './PricingBreakdownSidebar';
import { AddInventoryItemDialog } from './AddInventoryItemDialog';

// Legacy ceramic lines (saved under the old package-tier model) still carry a
// packageKey - NewQuotePage renders its label in the line subtitle. New lines
// are material-only and no longer use packages.
export const CERAMIC_PACKAGE_LABEL_KEYS: Record<'silver' | 'gold' | 'platinum', string> = {
  silver: 'pricing.ceramic.package.silver',
  gold: 'pricing.ceramic.package.gold',
  platinum: 'pricing.ceramic.package.platinum',
};

export interface CeramicLineOutput {
  serviceKind: 'ceramic';
  sizeClass: SizeClass;
  estimatedMinutes: number;
  lineTotalCents: number;
  breakdown: Record<string, unknown>;
  profileVersionId: string;
  vehicleLabel: string;
  vehicleCatalogTrimId: string | null;
  workspaceVehicleId: string | null;
  materialInventoryItemId?: string | null;
  materialQty?: number | null;
  materialUnit?: 'bottle' | null;
  reservedMaterialCostCents?: number | null;
}

// Ceramic is material-only: the selected Inventory item's price per bottle times
// the vehicle's required bottle count. No package tier / labor engine, so the
// line total IS the material cost (mirrors PpfLineBuilder's overrideResult shape
// purely so PricingBreakdownSidebar can render it).
function ceramicResult(materialCostCents: number): PricingResult {
  return {
    labor: [],
    estimatedMinutes: 0,
    filmFtX10: 0,
    materialCostCents,
    laborCostCents: 0,
    designCostCents: 0,
    printCostCents: 0,
    lineSubtotalCents: materialCostCents,
    breakdown: { entries: [{ label: 'material', cents: materialCostCents }] },
  };
}

export interface CeramicDraftState {
  vehicle: VehicleSelection | null;
  materialItemId: string;
  manualCeramicQty: string;
}

export function CeramicLineBuilder({
  workspaceId, onAddLine, initialState, onStateChange, customerVehicleHint = null, customerId = null, editingLine = null, onCancelEdit,
}: {
  workspaceId: string | null;
  onAddLine: (line: CeramicLineOutput) => void;
  // See VehicleAwareWrapLineBuilder: restore-on-mount + serialized-state emit
  // for the in-progress builder so leaving/reopening restores it open.
  initialState?: Record<string, unknown>;
  onStateChange?: (state: Record<string, unknown>) => void;
  customerVehicleHint?: CustomerVehicleHint | null;
  customerId?: string | null;
  editingLine?: DraftLine | null;
  onCancelEdit?: () => void;
}) {
  const { t } = useTranslation('quotes');
  const { formatMoney } = useLocaleFormatters();
  const { data: profile } = useCeramicPricingProfile(workspaceId);

  // Ceramic material comes from Inventory (category = 'ceramic'), same as vinyl
  // wrap's MaterialPicker - not a separate coating-product catalog.
  const { data: inventoryData } = useInventory(workspaceId, { category: 'ceramic' });
  const ceramicItems = inventoryData?.items ?? [];
  const { data: brandsData } = useBrands(workspaceId);
  const brandNameById = new Map((brandsData?.items ?? []).map((b): [string, string] => [b.id, b.name]));
  const itemBrandName = (item: InventoryItem) => (item.brandId && brandNameById.get(item.brandId)) || t('pricing.noBrand');

  const restore = (editingLine ? undefined : initialState) as Partial<CeramicDraftState> | undefined;

  const [vehicle, setVehicle] = useState<VehicleSelection | null>(restore?.vehicle ?? null);
  const [materialItemId, setMaterialItemId] = useState<string>(editingLine?.materialInventoryItemId ?? restore?.materialItemId ?? '');
  const [addInventoryOpen, setAddInventoryOpen] = useState(false);
  // Tracks an item created via the inline dialog so the "default to first
  // item" effect below doesn't clobber the selection once useInventory
  // refetches and the new item appears in ceramicItems.
  const [pendingSelectItemId, setPendingSelectItemId] = useState<string | null>(null);

  // See VehicleAwareWrapLineBuilder.tsx's identical effect for why: an edited
  // line only carries the vehicle's ids, not its full VehicleSelection.
  const { data: trimDetail } = useVehicleCatalogTrimDetail(workspaceId, editingLine?.vehicleCatalogTrimId ?? undefined);
  const { data: workspaceVehicleDetail } = useWorkspaceVehicleById(workspaceId, editingLine?.workspaceVehicleId ?? undefined);
  const resolvingEditVehicle = !!(editingLine?.vehicleCatalogTrimId || editingLine?.workspaceVehicleId) && !vehicle;

  useEffect(() => {
    if (vehicle) return;
    const resolved = trimDetail
      ? vehicleSelectionFromTrimDetail(trimDetail)
      : workspaceVehicleDetail
        ? vehicleSelectionFromWorkspaceVehicleItem(workspaceVehicleDetail)
        : null;
    if (resolved) setVehicle(resolved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimDetail, workspaceVehicleDetail]);

  // Default to the first stocked item once inventory loads, correcting away from
  // a selection that no longer exists (mirrors MaterialPicker's identical
  // "keep the item selection valid" effect).
  useEffect(() => {
    if (!inventoryData) return;
    if (pendingSelectItemId && ceramicItems.some((i) => i.id === pendingSelectItemId)) {
      setMaterialItemId(pendingSelectItemId);
      setPendingSelectItemId(null);
      return;
    }
    if (materialItemId && ceramicItems.some((i) => i.id === materialItemId)) return;
    setMaterialItemId(ceramicItems[0]?.id ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryData]);

  const selectedItem = ceramicItems.find((i) => i.id === materialItemId);
  const selectedBrand = selectedItem ? itemBrandName(selectedItem) : '';
  const brandOptions = Array.from(new Set(ceramicItems.map(itemBrandName))).sort();
  const itemOptionsForBrand = ceramicItems.filter((i) => itemBrandName(i) === selectedBrand);

  function handleBrandChange(brand: string) {
    const firstMatch = ceramicItems.find((i) => itemBrandName(i) === brand);
    setMaterialItemId(firstMatch?.id ?? '');
  }

  // Material consumption (§material-consumption): see PpfLineBuilder.tsx's
  // identical block for the full rationale.
  const vehiclePricingSource: VehiclePricingSource | null = vehicle?.catalogVehicleId ? 'catalog' : vehicle?.workspaceVehicleId ? 'workspace' : null;
  const vehiclePricingId = vehiclePricingSource === 'catalog' ? vehicle?.catalogVehicleId : vehicle?.workspaceVehicleId;
  const { data: serviceQuantities } = useVehicleServiceQuantities(
    workspaceId,
    vehiclePricingSource,
    vehiclePricingId ?? null,
  );
  const putServiceQuantity = usePutVehicleServiceQuantity(workspaceId, vehiclePricingSource, vehiclePricingId ?? null);
  const storedCeramicQty = serviceQuantities?.ceramic ?? null;
  const [manualCeramicQty, setManualCeramicQty] = useState<string>(restore?.manualCeramicQty ?? '');
  const materialQty = storedCeramicQty ?? (manualCeramicQty ? Number(manualCeramicQty) : null);
  const materialQtyValid = materialQty !== null && materialQty > 0;

  const materialCostCents = selectedItem && materialQtyValid
    ? Math.round(materialQty! * selectedItem.priceCentsPerUnit)
    : 0;

  const result = selectedItem && materialQtyValid ? ceramicResult(materialCostCents) : null;

  const canAdd = !!vehicle && !!profile && !!result && !!selectedItem && materialQtyValid;

  // Single source of truth for this line's output - see VehicleAwareWrapLineBuilder.
  const liveOutput: CeramicLineOutput | null = canAdd && vehicle && result && profile && selectedItem
    ? {
        serviceKind: 'ceramic',
        sizeClass: vehicle.sizeClass,
        estimatedMinutes: 0,
        lineTotalCents: materialCostCents,
        breakdown: result.breakdown as unknown as Record<string, unknown>,
        profileVersionId: profile.versionId,
        vehicleLabel: vehicle.label,
        vehicleCatalogTrimId: vehicle.catalogTrimId,
        workspaceVehicleId: vehicle.workspaceVehicleId,
        materialInventoryItemId: selectedItem.id,
        materialQty,
        materialUnit: 'bottle',
        reservedMaterialCostCents: materialCostCents > 0 ? materialCostCents : null,
      }
    : null;

  // Persist raw builder state (add mode, vehicle picked) - see VehicleAwareWrapLineBuilder.
  const draftState: CeramicDraftState = { vehicle, materialItemId, manualCeramicQty };
  const draftStateKey = JSON.stringify(draftState);
  useEffect(() => {
    if (editingLine || !vehicle) return;
    onStateChange?.(draftState as unknown as Record<string, unknown>);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftStateKey, editingLine]);

  function handleAddLine() {
    if (!liveOutput) return;
    if (storedCeramicQty === null && vehiclePricingSource && vehiclePricingId) {
      putServiceQuantity.mutate({ kind: 'ceramic', input: { quantity: materialQty! } });
    }
    onAddLine(liveOutput);
    toast.success(editingLine ? t('pricing.lineUpdatedToast') : t('pricing.ceramic.toastAdded'));

    if (!editingLine) {
      setVehicle(null);
      setManualCeramicQty('');
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <section className="flex flex-col gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-[.08em] text-muted">{t('pricing.step.vehicle')}</h3>
        {resolvingEditVehicle ? (
          <p className="text-sm text-muted">{t('pricing.loadingVehicle')}</p>
        ) : (
          <VehiclePicker workspaceId={workspaceId} selection={vehicle} onSelect={setVehicle} customerVehicleHint={customerVehicleHint} customerId={customerId} />
        )}
      </section>

      {vehicle ? (
        <>
          <section className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-[.08em] text-muted">{t('pricing.step.ceramicMaterial')}</h3>
              <button type="button" onClick={() => setAddInventoryOpen(true)} className="text-[11px] font-semibold text-accent-ink hover:underline">
                {t('pricing.material.addToInventory')}
              </button>
            </div>
            {!inventoryData ? (
              <p className="text-xs text-muted">{t('pricing.material.loadingStocked')}</p>
            ) : ceramicItems.length === 0 ? (
              <div className="flex flex-col gap-1.5 border border-border p-3">
                <p className="text-sm text-muted">{t('pricing.ceramic.noInventoryItems')}</p>
                <Link to="/inventory" className="w-fit text-xs font-semibold text-accent-ink underline">
                  {t('pricing.ceramic.addToInventoryLink')}
                </Link>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="ceramic-brand">{t('pricing.ceramic.brandLabel')}</Label>
                    <select
                      id="ceramic-brand"
                      value={selectedBrand}
                      onChange={(e) => handleBrandChange(e.target.value)}
                      className="h-9 border border-border bg-surface px-2 text-sm"
                    >
                      {brandOptions.map((b) => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="ceramic-item">{t('pricing.ceramic.itemLabel')}</Label>
                    <select
                      id="ceramic-item"
                      value={materialItemId}
                      onChange={(e) => setMaterialItemId(e.target.value)}
                      className="h-9 border border-border bg-surface px-2 text-sm"
                    >
                      {itemOptionsForBrand.map((i) => (
                        <option key={i.id} value={i.id}>{i.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {storedCeramicQty !== null ? (
                  <p className="text-sm text-ink">{t('pricing.materialConsumption.ceramicNeededLabel')}: {storedCeramicQty}</p>
                ) : (
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="ceramic-material-qty">{t('pricing.materialConsumption.ceramicNeededLabel')}</Label>
                    <Input
                      id="ceramic-material-qty"
                      type="number"
                      min="0"
                      step="any"
                      value={manualCeramicQty}
                      onChange={(e) => setManualCeramicQty(e.target.value)}
                      className="h-9 max-w-[10rem]"
                    />
                  </div>
                )}
                {selectedItem && materialQtyValid ? (
                  <p className="text-xs text-muted">
                    {t('pricing.materialConsumption.breakdownRow', {
                      qty: materialQty,
                      unit: selectedItem.unit,
                      price: formatMoney(selectedItem.priceCentsPerUnit),
                      cost: formatMoney(materialCostCents),
                    })}
                  </p>
                ) : null}
              </>
            )}
          </section>

          {result ? <PricingBreakdownSidebar result={result} /> : null}

          <div className="flex items-center gap-2">
            <Button type="button" disabled={!canAdd} onClick={handleAddLine}>
              {editingLine ? t('pricing.saveChanges') : t('pricing.addService')}
            </Button>
            {editingLine ? (
              <Button type="button" variant="secondary" onClick={onCancelEdit}>
                {t('pricing.cancelEdit')}
              </Button>
            ) : null}
          </div>
        </>
      ) : (
        <p className="text-sm text-muted">{t('pricing.pickVehicle')}</p>
      )}

      <AddInventoryItemDialog
        open={addInventoryOpen}
        onOpenChange={setAddInventoryOpen}
        workspaceId={workspaceId}
        category="ceramic"
        onCreated={(itemId) => {
          setPendingSelectItemId(itemId);
          setMaterialItemId(itemId);
        }}
      />
    </div>
  );
}
